import React from 'react';
import { usePermissions } from '@/hooks/usePermissions';
import { auditLogger } from '@/services/auth/auditLogger';

interface PageActionGateProps {
  pageId: string;
  actionId: string;
  children: React.ReactNode;
  fallback?: React.ReactNode;
  showError?: boolean;
  onUnauthorized?: () => void;
}

/**
 * Component that conditionally renders children based on page action permissions
 * Following Ant Design Pro authority management patterns
 *
 * @example
 * <PageActionGate pageId="page:payout" actionId="action:filterBtn">
 *   <FilterForm />
 * </PageActionGate>
 */
const PageActionGate: React.FC<PageActionGateProps> = ({
  pageId,
  actionId,
  children,
  fallback = null,
  showError = false,
  onUnauthorized,
}) => {
  const { canPerformPageAction, loading } = usePermissions();

  const hasPermission = canPerformPageAction(pageId, actionId);

  React.useEffect(() => {
    if (!loading && !hasPermission) {
      // Log unauthorized access attempt
      auditLogger.logUserAction('UNAUTHORIZED_ACTION_ACCESS', {
        pageId,
        actionId,
        timestamp: new Date().toISOString(),
      });

      if (onUnauthorized) {
        onUnauthorized();
      }
    }
  }, [hasPermission, pageId, actionId, loading, onUnauthorized]);

  if (loading) {
    return <div>Loading...</div>;
  }

  if (!hasPermission) {
    if (showError) {
      return (
        <div
          style={{
            padding: '20px',
            textAlign: 'center',
            color: '#999',
            border: '1px dashed #d9d9d9',
            borderRadius: '6px',
            margin: '16px 0',
          }}
        >
          <p>You don't have permission to perform this action.</p>
        </div>
      );
    }

    return <>{fallback}</>;
  }

  return <>{children}</>;
};

export default PageActionGate;
